// The condition report's rating scale, at the top of the Condition tab, and
// changing it (#473; MJ, 2026-10-23): with nothing rated it just switches;
// with ratings it asks first, saying how many it clears. Works offline.
import { useEffect, useRef, useState } from 'react';
import { Button, ConfirmDialog } from '@higgins/ui';
import { clearingText, ratingsToClear, scaleOf, staleRatings } from '../lib/condition';
import { changeScale, clearStaleRatings } from '../lib/condition-scale';
import type { DeviceRecord } from '../lib/device-db';
import { ScalePicker } from './condition-start';

export function ConditionScale({ visit, records }: { visit: DeviceRecord; records: readonly DeviceRecord[] }) {
  const current = scaleOf(visit.fields.hig_ratingscale).label;
  const [choosing, setChoosing] = useState(false);
  const [asking, setAsking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const changeButton = useRef<HTMLButtonElement>(null);
  const toClear = ratingsToClear(records);

  // This device's ratings made before it heard the scale changed are cleared, as the change cleared everyone else's.
  const stale = staleRatings(records, visit.fields.hig_ratingscale).length;
  useEffect(() => {
    if (stale) void clearStaleRatings(visit, records).catch(console.error);
  }, [stale, visit, records]);

  async function change(label: string) {
    setBusy(true);
    try {
      await changeScale(visit, records, label);
      setChoosing(false);
      setAsking(null);
    } finally {
      setBusy(false);
    }
  }

  function choose(label: string) {
    if (label === current) return setChoosing(false);
    if (toClear.ratings) return setAsking(label);
    void change(label);
  }

  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-xs text-slate-500">Rating scale</span>
        <span className="text-sm text-slate-900">{current}</span>
        <Button ref={changeButton} variant="outline" size="sm" className="ml-auto min-h-11" onClick={() => setChoosing(!choosing)} aria-expanded={choosing}>
          {choosing ? 'Cancel' : 'Change'}
        </Button>
      </div>
      {choosing && (
        <div className="pb-1">
          <ScalePicker value={current} onChange={choose} disabled={busy} />
          <p className="mt-1 text-xs text-slate-500">
            {toClear.ratings ? `Changing it clears the ratings (${toClear.ratings}). Defects, notes and photos stay.` : 'Nothing is rated yet, so nothing is cleared.'}
          </p>
        </div>
      )}
      <ConfirmDialog
        open={asking !== null}
        onOpenChange={(open) => !open && setAsking(null)}
        title={`Change the scale to ${asking ?? ''}?`}
        description={`${clearingText(toClear)} Defects, notes and photos stay. Rate the surfaces again on the new scale.`}
        confirmLabel="Clear and change"
        busyLabel="Changing…"
        destructive
        busy={busy}
        onConfirm={() => asking && void change(asking)}
        returnFocus={changeButton}
      />
    </div>
  );
}
